import {
  concretiseParams,
  updateSearchParamsWithFilters,
} from "../filters/filters"
import { updateSearchParamsWithPagination } from "../../page"
import { API_BASE_URL } from "../../app/config"
import { getToken, handleSignIn } from "./middleware"

/**
 * Get user accounts
 * @param filers  // filters to apply
 * @param page  // pagination settings
 * @returns {Promise<any>}
 */
export const getUserAccounts = async ({ filter = {}, page = {} }) => {
  // Build parameter set
  const params = new URLSearchParams()
  // Add the relevant filters
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones
  concretiseParams(params)
  // Pagination control
  updateSearchParamsWithPagination(params, page)

  let resp = await fetch(
    `${API_BASE_URL}/api/iam/accounts?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Get a User account
 * @param id the user account ID
 * @returns {Promise<any>}
 */
export const getUserAccountById = async (id) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}`, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    redirect: "error",
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a user account to active or inactive
 * @param id
 * @param active a boolean value
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setUserAccountActive = async ({ id, active, _etag }) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/active`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      id: id,
      active: active,
      _etag: _etag,
    }),
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a user account display name
 * @param id
 * @param name display name for the user account
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setUserDisplayName = async ({ id, name, _etag }) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/name`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      id: id,
      display_name: name,
      _etag: _etag,
    }),
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Create a new user account
 *
 * @param req  // the details of the user account
 * @returns {Promise<any>}
 */
export const createUserAccount = async (req) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(req),
  })
  // TODO replace with handleSignIn when we have fixed it to properly handle errors
  if (!resp.ok) {
    throw await resp.json()
  }

  return resp.json()
}

/**
 * Add a role to a user account
 * @param id
 * @param role the role itself
 * @param ctx the role ctx, typically the url of the organisation
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const addRole = async ({ id, role, ctx, _etag }) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/roles`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      id,
      role,
      ctx,
      _etag,
    }),
  })
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Remove a role from a user account
 * @param id
 * @param role the role itself
 * @param ctx the role ctx, typically the url of the organisation
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const removeRole = async ({ id, role, ctx, _etag }) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/roles`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      id,
      role,
      ctx,
      _etag,
    }),
  })
  resp = handleSignIn(resp)
  if (!resp.ok) {
    throw new Error(resp?.error)
  }
  return resp.json()
}

/**
 * Set a user account password (admin only service)
 * @param id
 * @param current_password current password for the user account
 * @param password password for the user account
 * @param confirmation password confirmation for the user account
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setUserAccountPassword = async ({
  id,
  current_password,
  password,
  confirmation,
  _etag,
}) => {
  const token = getToken()
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/pwd`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      // Note: Token is optional, this can also be called by an unauthenticated
      // user in order to reset password before signing in.
      Authorization: token ? `Bearer ${getToken()}` : undefined,
    },
    body: JSON.stringify({
      id,
      current_password,
      password,
      confirmation, // TODO: Bit weird to send the duplicate to back-end?
      _etag, // Optional
    }),
  })

  if (!resp.ok) {
    throw await resp.json()
  }
  return resp.json()
}

/**
 * Login with username/password, obtains token.
 *
 * @param req  // the login details
 * @returns {Promise<string>}
 */
export const doAuth = async (req) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(req),
  })

  // If it was unsuccessful, do a best-effort to decode the body as JSON
  // and throw it as an error, as it may contain information such as "user must
  // first reset their password"
  if (!resp.ok) {
    throw await resp.json()
  }
  return resp.json()
}

/**
 * Indicate that the user must reset their password on the next login
 * @param id
 * @returns {Promise<any>}
 */
export const setPwdResetRequired = async ({ ids }) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/iam/accounts/password-reset-required`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(ids),
    }
  )
  resp = handleSignIn(resp)
  if (!resp.ok) {
    throw new Error(resp?.error)
  }
}

/**
 * Get access logs
 *
 * @param filers  // filters to apply
 * @param page  // pagination settings
 * @returns {Promise<any>}
 */
export const getAccessLogs = async ({ filter = {}, page = {} }) => {
  // Build parameter set
  const params = new URLSearchParams()
  // Add the relevant filters
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones
  concretiseParams(params)
  // Pagination control
  updateSearchParamsWithPagination(params, page)

  let resp = await fetch(
    `${API_BASE_URL}/api/iam/access-logs?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/** Produces a URL that that will download a CSV file
 * of the access logs, filtered by the given filters. */
export const getAccessLogsCSVURL = (filter, authToken) => {
  const params = new URLSearchParams()
  filter.offset = 0
  filter.limit = 100000000
  updateSearchParamsWithFilters(params, filter)
  concretiseParams(params)
  params.set("format", "csv")
  return `${API_BASE_URL}/api/iam/access-logs?${params.toString()}${
    authToken ? "&token=" + encodeURIComponent(authToken) : ""
  }`
}

/** Enrolls the requested user for using 2FA with TOTP. Note: This neither
 * requires nor releases this user form having to use a TOTP to login,
 * it merely sets (or resets) the user's private key, in preparation for
 * sharing this with the user in order to enroll in an authenticator or
 * app before turning it on. */
export const enrollUserTOTP = async (id) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/iam/accounts/${id}/totp/enrollment`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({}), // Sending empty object, API is a bit strange
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/** Sets whether the indicated user requires login with 2FA (true) or not (false) */
export const setTOTPRequired = async (id, required) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/iam/accounts/${id}/totp/required`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ required }),
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/** Retrieves the key (secret) for the indicated user, for manually setting up
    an authenticator app or device. */
export const getTOTPSecret = async (id) => {
  let resp = await fetch(`${API_BASE_URL}/api/iam/accounts/${id}/totp`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/** Returns a URL for a QR code to enroll the given user */
export const getTOTPEnrollmentImageURL = (id) =>
  `${API_BASE_URL}/api/iam/accounts/${id}/totp/enrollment.png?token=${getToken()}`

/** Tests that a given TOTP is valid as of right now for the indicated user ID */
export const verifyTOTP = async (id, code) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/iam/accounts/${id}/totp/validity`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ code }),
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}
